import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { getProductRecommendations } from '../../../actions/shop';
import { getTitle } from '../../../helpers/admin/productHelper';
import { formatProductReviews } from '../../../helpers/shop/shopHelper';
import { resolveProductRecommendations } from '../../../helpers/shop/productRecommendationHelper';
import {
    trackRecommendationClick,
    trackRecommendationImpression
} from '../../../helpers/shop/productRecommendationAnalytics';

import shape from '../../../assets/resources/shape.png';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

import {
    Box,
    Container,
    Grid,
    IconButton,
    Link,
    Skeleton,
    Stack,
    Typography
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const RecommendationSkeleton = () => (
    <Container maxWidth="xxl" sx={{ mt: 5 }}>
        <Skeleton width={300} height={42} sx={{ mx: 'auto', mb: 3 }} />
        <Grid container spacing={3}>
            {[0, 1, 2, 3].map(item => (
                <Grid item xl={3} lg={3} md={6} sm={6} xs={12} key={item}>
                    <Skeleton variant="rectangular" height={220} />
                    <Skeleton height={30} sx={{ mt: 1 }} />
                    <Skeleton width="45%" height={28} sx={{ mx: 'auto' }} />
                </Grid>
            ))}
        </Grid>
    </Container>
);

const ProductRecommendations = ({ productId, variantId }) => {
    const navigate = useNavigate();
    const { products, cart } = useSelector(state => state.shop);
    const [rawItems, setRawItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const impressionKey = useRef('');

    useEffect(() => {
        let active = true;
        setRawItems([]);
        if (!productId || !variantId)
            return () => { active = false; };

        setLoading(true);
        getProductRecommendations(productId, variantId)
            .then(result => {
                if (active)
                    setRawItems(result.status ? result.items : []);
            })
            .finally(() => {
                if (active)
                    setLoading(false);
            });
        return () => { active = false; };
    }, [productId, variantId]);

    const items = useMemo(() => resolveProductRecommendations({
        recommendations: rawItems,
        products,
        cartItems: cart.item,
        sourceProductId: productId
    }), [rawItems, products, cart.item, productId]);

    useEffect(() => {
        if (loading || items.length < 2)
            return;
        const key = `${variantId}:${items.map(item => item.variant_id).join(',')}`;
        if (impressionKey.current === key)
            return;
        impressionKey.current = key;
        trackRecommendationImpression({
            sourceProductId: productId,
            sourceVariantId: variantId,
            items
        });
    }, [items, loading, productId, variantId]);

    const openProduct = item => () => {
        trackRecommendationClick({
            sourceProductId: productId,
            sourceVariantId: variantId,
            item
        });
        navigate(`/tienda/producto/${item.url}/${item.barcode}`);
    };

    if (loading)
        return <RecommendationSkeleton />;
    if (items.length < 2)
        return null;

    return (
        <Container maxWidth="xxl" sx={{ mt: 5 }}>
            <Typography variant="h5" color="text.primary"
                sx={{ fontSize: '26px', mb: 5 }} textAlign="center">
                También te puede interesar
            </Typography>
            <Grid container spacing={2} sx={{ textAlign: 'center' }}>
                <Grid item xl={1} lg={1} md={1} sm={2} xs={2} sx={{ my: 'auto' }}>
                    <IconButton className="recommendations-swiper-button-prev" size="small"
                        aria-label="Recomendación anterior">
                        <ChevronLeftIcon />
                    </IconButton>
                </Grid>
                <Grid item xl={10} lg={10} md={10} sm={8} xs={8}>
                    <Swiper
                        breakpoints={{
                            0: { slidesPerView: 1 },
                            610: { slidesPerView: 2 },
                            950: { slidesPerView: 3 },
                            1200: { slidesPerView: 4 }
                        }}
                        spaceBetween={40}
                        navigation={{
                            nextEl: '.recommendations-swiper-button-next',
                            prevEl: '.recommendations-swiper-button-prev'
                        }}
                        pagination={{ clickable: true, dynamicBullets: true }}
                        modules={[Navigation, Pagination]}>
                        {items.map(item => {
                            const title = getTitle(item.title, item.variant_title);
                            const { review_prom, review_count } = formatProductReviews(item.review_summary);
                            const promotion = item.item_promotion || {};
                            const hasDiscount = ['DescuentoProducto', 'DescuentoMarca']
                                .includes(promotion.promo);
                            return (
                                <SwiperSlide key={item.variant_id} style={{ textAlign: 'center' }}>
                                    <Link component="button" onClick={openProduct(item)}
                                        aria-label={`Ver ${title}`} sx={{ width: '100%' }}>
                                        <Box sx={{ position: 'relative' }}>
                                            <img src={item.img.id} width="100%" height="220"
                                                style={{ objectFit: 'contain' }} alt={item.img.alt} />
                                            {promotion.promo && (
                                                <div className="shop_top-image" title={promotion.description}>
                                                    <img src={shape} alt="" width="75"
                                                        style={{ pointerEvents: 'none' }} />
                                                    <div className="shop_centered_image">
                                                        <Typography sx={{
                                                            color: '#fff',
                                                            fontSize: promotion.fontSize,
                                                            fontWeight: 'bold'
                                                        }}>
                                                            {promotion.text}
                                                        </Typography>
                                                    </div>
                                                </div>
                                            )}
                                        </Box>
                                    </Link>
                                    <Link component="button" onClick={openProduct(item)}
                                        underline="hover" color="inherit">
                                        <Typography variant="body1" gutterBottom sx={{ fontWeight: 600 }}>
                                            {title}
                                        </Typography>
                                    </Link>
                                    <Box className="ratings-shop" sx={{ mt: 1 }}
                                        aria-label={`${review_count} reseñas`}>
                                        {Array.from({ length: 5 }, (_, index) => (
                                            <i className={index < review_prom ? 'fas fa-star' : 'far fa-star'}
                                                style={index < review_prom ? { color: '#FFA41C' } : {}}
                                                key={index}></i>
                                        ))}
                                    </Box>
                                    {hasDiscount ? (
                                        <Stack direction="row" justifyContent="center"
                                            alignItems="center" spacing={1} sx={{ mt: 1 }}>
                                            <Typography variant="h6" sx={{ textDecoration: 'line-through' }}>
                                                ${item.price}
                                            </Typography>
                                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#F90606' }}>
                                                ${promotion.price}
                                            </Typography>
                                        </Stack>
                                    ) : (
                                        <Typography variant="h6" sx={{ mt: 1, fontWeight: 600, color: '#F90606' }}>
                                            ${item.price}
                                        </Typography>
                                    )}
                                </SwiperSlide>
                            );
                        })}
                    </Swiper>
                </Grid>
                <Grid item xl={1} lg={1} md={1} sm={2} xs={2} sx={{ my: 'auto' }}>
                    <IconButton className="recommendations-swiper-button-next" size="small"
                        aria-label="Siguiente recomendación">
                        <ChevronRightIcon />
                    </IconButton>
                </Grid>
            </Grid>
        </Container>
    );
};

export default ProductRecommendations;
